<template>
  <div class="starred-resource-card-list">
    <div v-if="rowsSelected" class="header">
      <selection-bar :count="rowsSelected" @clear="handleClearSelection" />
    </div>
    <div class="content">
      <template v-if="resultList">
        <card-list size="grid">
          <starred-resource-card-item
            v-for="item in resultList"
            :key="item.id"
            :resource-type="props.resourceType"
            :item="item"
            :size="cardSize"
            :creation-params="props.creationParams"
            @handle-selected="handleSelected"
            @selection-changed="handleSelectionChanged"
            @selection-saved="handleSelectionSaved"
            @resource-unstarred="handleResourceUnstarred"
            @selected="handleItemSelected"
          />
        </card-list>
      </template>
      <template v-else>
        <empty-placeholder v-if="!props.hasStarredResources">
          <template #icon>
            <EmptyStarred />
          </template>
          {{ t("search_result_list_no_starred_resource_message") }}
        </empty-placeholder>

        <empty-placeholder v-else>
          <template #icon>
            <SearchEmpty />
          </template>
          {{ t("search_result_list_no_result_message") }}
        </empty-placeholder>
      </template>
    </div>
    <div class="footer">
      <cv-pagination
        v-if="resultList"
        :page-sizes="[props.maxPageSize]"
        :actual-items-on-page="props.maxPageSize"
        :forwards-button-disabled="disableForwardsButton"
        :backwards-button-disabled="disableBackwardsButton"
        :page="props.currentPage"
        @change="handlePageChange"
      >
        <template #range-text="{ scope }">
          {{ scope.start }}
          -
          {{ Math.min(scope.end, props.totalRecords) }}
          of {{ props.totalRecords }}
        </template>
      </cv-pagination>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { isDefined } from "@utils/var.check";
import { RESOURCE_TYPE } from "@modules/editor/service/editor.constant";
import EmptyStarred from "@images/placeholder/empty-states/EmptyStarred.svg?component";
import SearchEmpty from "@images/placeholder/empty-states/SearchEmpty.svg?component";
import StarredResourceCardItem from "@modules/editor/components/lesson/StarredResourceCardItem.vue";
import store from "@modules/search/components/SearchStore";
import type {
  AddLessonFromLibraryContentSubpageSelectionEvent,
  LessonSelectionMode,
} from "@modules/resource-search/resource-search-types.ts";

interface Props {
  resourceType?: string | null;
  addFromType?: string | null;
  resourceData?: object[];
  mode?: string;
  titles?: string[];
  addFromLocation?: string;
  totalRecords: number;
  createNewAssignment?: boolean;
  isFromPlanner?: boolean;
  isAttachingAttachment?: boolean;
  selectionMode?: LessonSelectionMode;
  addFromMpqChildLayer?: boolean;
  addFromMpqSubLayer?: boolean;
  creationParams?: object;
  maxPageSize: number;
  currentPage: number;
  searchFilters?: object;
  hasStarredResources?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  handleSelected: [data: unknown];
  selectionChanged: [];
  selectionSaved: [data: unknown];
  pageChange: [page: number];
  resourceUnstarred: [data: unknown];
  selected: [data: AddLessonFromLibraryContentSubpageSelectionEvent];
}>();

const { t } = useI18n();

// Provide search context
provide("searchContext", {
  mode: props.mode,
  title: props.titles,
  resourceType: props.resourceType,
  addFromType: props.addFromType,
  addFromLocation: props.addFromLocation,
  createNewAssignment: props.createNewAssignment,
  isFromPlanner: props.isFromPlanner,
  isAttachingAttachment: props.isAttachingAttachment,
  selectionMode: props.selectionMode,
  addFromMpqChildLayer: props.addFromMpqChildLayer,
  addFromMpqSubLayer: props.addFromMpqSubLayer,
});

// State
const selectedList = ref<object>({});

// Computed
const resultList = computed(() => {
  if (
    isDefined(props.resourceData) &&
    props.resourceData !== null &&
    props.resourceData?.length > 0
  ) {
    return props.resourceData;
  } else {
    return false;
  }
});

const pageSize = computed(() => props.resourceData?.length);

const cardSize = computed(() => (props.resourceType === RESOURCE_TYPE.LESSON ? "large" : "xsmall"));

const rowsSelected = computed(() => Object.entries(selectedList.value).length);

const disableBackwardsButton = computed(() => {
  if (props.totalRecords <= 0) {
    return true;
  }
  return props.currentPage <= 1;
});

const disableForwardsButton = computed(() => {
  if (props.totalRecords <= props.maxPageSize) {
    return true;
  } else {
    let items = props.currentPage * props.maxPageSize;
    if (pageSize.value < props.maxPageSize) {
      items -= props.maxPageSize - pageSize.value;
    }
    return items >= props.totalRecords;
  }
});

// Watchers
watch(
  () => props.searchFilters,
  () => {
    resetPageNumber();
  },
);

// Methods
const handleSelected = (data: unknown) => {
  emit("handleSelected", data);
};

const handleItemSelected = (data: AddLessonFromLibraryContentSubpageSelectionEvent) => {
  emit("selected", data);
};

const handleSelectionChanged = () => {
  selectedList.value = store.getSelectionList();
  emit("selectionChanged");
};

const handleSelectionSaved = (data: unknown) => {
  emit("selectionSaved", data);
};

const handleClearSelection = () => {
  store.clearSelectionList();
  selectedList.value = store.getSelectionList();
  emit("selectionChanged");
};

const handleResourceUnstarred = (data: unknown) => {
  resetPageNumber();
  emit("resourceUnstarred", data);
};

const handlePageChange = (event: { page: number }) => {
  emit("pageChange", event.page);
};

const resetPageNumber = () => {
  emit("pageChange", 1);
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.starred-resource-card-list {
  background-color: $ui-01;
  margin: 0 (-$spacing-05) (-$spacing-05);
  padding: $spacing-05;

  > .header {
    .selection-bar {
      margin-bottom: $spacing-06;
    }
  }

  > .content {
    .card-list {
      margin-top: $spacing-03;
    }
  }

  .card-component.question {
    &:has(li.subject) {
      .metadata {
        flex-wrap: nowrap;

        li {
          flex: 0 0 auto;

          &.subject {
            @include line-clamp(1);
            flex: 1 1 auto;
            margin-top: -1px; // to counter the text render line height differences on bolded text
          }
        }
      }
    }
  }
}
</style>
